import { useMemo } from 'react';
import { useTheme } from '@material-ui/core/styles';
import { t } from '@lingui/macro';

/**
 * Ported from portal/ui testComparisonChartBuilder.service.js
 */

import { assignIn, chain, difference, filter, find, get, max, reject, some } from 'lodash';

import { getDefaultMetricColor, getStatusColor, lightenColor } from '../../../utilities/ColorUtilities';
import { percentFormatter } from '../../../utilities/FormattingUtilities';
import { getMetricNameForBuiltInTest, NATIVE_TEST_TYPE_GROUPS } from '../../../utilities/TestTypeConstants';

const WEB_TEST_METRIC_MAPPING = {
    availability: 'status',
    response_time: 'avgResponse',
    namelookup_time: 'avgDns',
    network_delay: 'avgNetwork',
    ssl_delay: 'avgSsl',
    client_delay: 'avgClient',
    app_delay: 'avgApp',
    server_delay: 'avgServer',
    response_code: 'avgResponseCode',
};

const VOIP_TEST_METRIC_MAPPING = {
    availability: 'status',
    lqmos_avg: 'avgLqmosRx',
    tos: 'avgTos',
    codec_pt: 'avgCodecPt',
    rtt_avg: 'avgRttRx',
    rx_pct_loss: 'avgPctLossRx',
    rx_jitter_avg: 'avgJitterRx',
    dial_delay: 'avgDialDelay',
    ring_delay: 'avgRingDelay',
    error_code: 'avgErrorCode',
    call_duration: 'avgCallDuration',
    sip_status: 'avgSipStatus',
    tx_lqmos_avg: 'avgLqmosTx',
    tx_codec_pt: 'avgCodecPt',
    tx_rtt_avg: 'avgRttTx',
    tx_pct_loss: 'avgPctLossTx',
    tx_jitter_avg: 'avgJitterTx',
    tx_rtcp: '', // what does this map to in the query engine?
};

export const VARIANT_OVERLAY = 'overlay';
export const VARIANT_BREAKDOWN = 'breakdown';

export const BEFORE = 'before';
export const AFTER = 'after';
export const DELTA = 'delta';

/**
 * Builds the series for a before/after dataset as an overlay instead of using separate stacks
 *
 * @param {Object} raw
 * @return {Object[]}
 */
export function useTestMetricOverlaySeries(raw, paletteType) {
    const theme = useTheme();
    return useMemo(
        () => buildComparisonSeries(raw, { variant: VARIANT_OVERLAY, paletteType, theme }),
        [raw, paletteType, theme]
    );
}

/**
 * Builds the series for a before/after dataset as an breakdown, to be displayed as stacked columns
 *
 * @param {Object} raw
 * @return {Object[]}
 */
export function useTestMetricBreakdownSeries(raw, paletteType) {
    const theme = useTheme();
    return useMemo(
        () => buildComparisonSeries(raw, { variant: VARIANT_BREAKDOWN, paletteType, theme }),
        [raw, paletteType, theme]
    );
}

export function getYAxisTitle(testMeta) {
    let title;
    let units;
    if (testMeta?.testTypeGroup === NATIVE_TEST_TYPE_GROUPS.WEB) {
        title = 'Delay';
        units = 's';
    } else if (testMeta?.testTypeGroup === NATIVE_TEST_TYPE_GROUPS.VOIP) {
        title = 'MOS';
    } else {
        const primaryMetric = testMeta?.primaryMetric;
        title = primaryMetric?.title;
        units = primaryMetric?.units;
    }
    return units ? `${title} (${units})` : title;
}

/**
 * Creates an array of series for a before/after dataset
 */
function buildComparisonSeries(raw, options) {
    if (!raw) return undefined;
    const testsMeta = raw.data.map((row) => extractTestMetadata(row, options));

    testsMeta.forEach(mapAndAttachMetricData);

    const beforeSeries = buildTestMetricSeries(testsMeta, BEFORE, options);
    const afterSeries = buildTestMetricSeries(testsMeta, AFTER, options);
    // We want to show a delta for each metric in the breakdown chart tooltip, so calculate a delta series when
    // in breakdown variant.
    const deltaSeries = options.variant === VARIANT_BREAKDOWN ? buildTestMetricSeries(testsMeta, DELTA, options) : [];

    return [...beforeSeries, ...afterSeries, ...deltaSeries];
}

/**
 * Creates an array of series for all of the tests
 */
function buildTestMetricSeries(testsMeta, dataSource, options) {
    const metricSeries = [];
    const metricSeriesMap = {};

    testsMeta.forEach((testMeta) =>
        testMeta.metrics.forEach((metric, metricIdx) => {
            const { id } = metric;
            let series = metricSeriesMap[id];
            const metricValue = testMeta?.dataValues[dataSource][metricIdx];

            if (!series) {
                metricSeriesMap[id] = buildMetricSeries(testMeta, metric, dataSource, options);
                series = metricSeriesMap[id];
                metricSeries.push(series);
            }

            series.data.push({
                name: testMeta.testName, // <-- category name
                testId: testMeta.testId,
                testMeta,
                deleted: testMeta.isTestDeleted,
                y: metricValue,
                value: metricValue,
                dataSource,
                metric,
            });
        })
    );

    return metricSeries;
}

export function getSeriesName(metric, isBuiltInTest) {
    // For web & VoIP tests, translate metric.name
    if (isBuiltInTest) return getMetricNameForBuiltInTest(metric.name);
    // For custom tests, use use metric.title
    return metric.title;
}

/**
 * Builds a series object for a given metric. This function does not map any data
 */
function buildMetricSeries(testMeta, metric, dataSource, options) {
    const { testTypeGroup } = testMeta;
    const isWebTest = testTypeGroup === NATIVE_TEST_TYPE_GROUPS.WEB;
    const isVoipTest = testTypeGroup === NATIVE_TEST_TYPE_GROUPS.VOIP;

    const isOverlay = options.variant === VARIANT_OVERLAY;
    const isAfter = dataSource === AFTER;
    const isDelta = dataSource === DELTA;
    const { paletteType = 'light' } = options;

    const series = {
        name: getSeriesName(metric, isWebTest || isVoipTest),
        metricName: metric.name,
        color: metric.color,
        metricId: metric.id,
        testMeta,
        mapping: false,
        showInLegend: isOverlay || isAfter,
        visible: !isDelta,
        cursor: 'pointer',
        stack: dataSource,
        dataSource,
        data: [],

        dataLabels: buildMetricSeriesDataLabels(testMeta, metric, dataSource, options),
    };

    // ok... the web test response time does not have a color associated with it.
    if (isWebTest && metric.name === 'response_time') {
        series.color = getDefaultMetricColor(0, paletteType);
    }

    // ok, now let's do some variant-dependent stuff to the series. After series should be smaller and lighter
    // for overlay charts.

    if (isOverlay) {
        series.name = `${series.name} ${isAfter ? t`After` : t`Before`}`;
    }

    if (isOverlay && isAfter) {
        series.color = lightenColor(series.color, 0.15);

        series.pointPadding = 0.35;
    }

    return series;
}

/**
 * Builds the dataLabels highcharts configuration for a metric's series.
 *
 * @param {Object} testMeta
 * @param {Object} metric
 * @param {String} dataSource
 * @param {Object} options
 * @return {Object}
 */
function buildMetricSeriesDataLabels(testMetaUnused, metric, dataSource, options) {
    // For overlay charts, datalabels are enabled for both before/after, and make the datalabel blank if it's not the highest point.
    // For breakdown charts, datalabels are enabled ONLY for the after
    const disabled = options.variant === VARIANT_BREAKDOWN && dataSource === BEFORE;
    const { paletteType, theme } = options;

    return {
        align: 'center',
        enabled: !disabled,
        useHTML: true,
        allowOverlap: true,
        padding: 2,
        position: 'center',
        overflow: 'none',
        crop: false,
        inside: false,
        formatter: dataLabelFormatter,
    };

    function dataLabelFormatter() {
        const { point } = this;
        const { testMeta } = point.options;

        if (isLabelVisible(point)) {
            const { delta } = testMeta;
            const pctFormattedDelta = percentFormatter(delta);
            const negative = testMeta.hasInvertedThreshold ? delta > 0 : delta < 0;

            const greenOrRed = negative ? 'green' : 'red';
            const colorName = pctFormattedDelta === '0%' ? theme?.palette?.text?.primary : greenOrRed;

            const arrowIcon = delta < 0 ? 'arrow_drop_down' : 'arrow_drop_up';
            return `
                <div class="highcharts-datalabel-callout" style="color: ${getStatusColor(
                    colorName,
                    paletteType
                )}; text-align: center">
                    <i class="material-icons">${delta === 0 ? '' : arrowIcon}</i>
                    <span>${pctFormattedDelta}</span>
                </div>`;
        }
    }

    function isLabelVisible(point) {
        const chartSeries = point.series.chart.series;
        const { series } = point;
        const pointTestMeta = point.options.testMeta;

        if (options.variant === VARIANT_OVERLAY) {
            const [otherSeries] = reject(chartSeries, (s) => s === series);

            // if there are only two series, and one of them is off, then the series we are on should display the label
            if (!otherSeries.visible) {
                return true;
            }

            return point.options.value === max([...pointTestMeta.dataValues.before, ...pointTestMeta.dataValues.after]);
        }

        if (options.variant === VARIANT_BREAKDOWN && dataSource === AFTER) {
            const seriesGroup = filter(chartSeries, { visible: true, options: { stack: AFTER } });
            return seriesGroup[0] === series;
        }
    }
}

/**
 * Extracts the data we need from the test its testType into a single object which makes
 * overall chart configuration a bit easier for us
 *
 * @param {Object} testRow
 * @param {Object} options
 * @return {Object}
 */
function extractTestMetadata(testRow, options) {
    const { test, before, after } = testRow;
    const { name: testName, uuid: testId, typeNode: testType, deleted: isTestDeleted } = test;
    const { group: testTypeGroup, name: testTypeName, uuid: testTypeId, metrics, thresholds } = testType;
    const primaryMetric = findPrimaryMetric(metrics);

    const metricsFiltered = filterMetrics(metrics, testTypeGroup, options);

    const hasInvertedThreshold = !!find(thresholds, { inverted: true });

    return {
        testId,
        testName,
        testTypeName,
        testTypeId,
        testTypeGroup,
        isTestDeleted,
        metrics: metricsFiltered,
        primaryMetric,
        thresholds,
        testData: { before, after },
        hasInvertedThreshold,
    };
}

function calculateDelta(before, after) {
    // before can be 0, which gives us Infinity
    if (after != null && before != null && before !== 0) {
        return ((after - before) / before) * 100;
    }
    return NaN;
}

/**
 * Given an extracted test object, this method maps all of the before/after values for a given test
 * and also calculates totals and deltas for that test
 *
 * @param {Object} testMeta
 */
function mapAndAttachMetricData(testMeta) {
    const { metrics, primaryMetric, testTypeGroup, testData } = testMeta;

    const beforeValues = metrics.map((metric) => getMetricValue(metric, testTypeGroup, testData?.before));
    const afterValues = metrics.map((metric) => getMetricValue(metric, testTypeGroup, testData?.after));
    const deltaValues = beforeValues.map((beforeValue, index) => calculateDelta(beforeValue, afterValues[index]));

    // Determine the delta using the primary metric for the test type.
    const before = getMetricValue(primaryMetric, testTypeGroup, testData.before);
    const after = getMetricValue(primaryMetric, testTypeGroup, testData.after);
    const delta = calculateDelta(before, after);

    assignIn(testMeta, {
        delta,
        dataValues: {
            before: beforeValues,
            after: afterValues,
            delta: deltaValues,
        },
    });
}

/**
 * Given a metric and testType, this pulls the value for that metric from `data`
 *
 * @param {Object} metric
 * @param {String} testTypeGroup
 * @param {Object} data
 * @return {Number}
 */
function getMetricValue(metric, testTypeGroup, data) {
    const mapping = getMetricMapping(metric, testTypeGroup);

    // This is goofy, but our VoIP & custom test metrics are automagically scaled correctly and web isn't:
    const scalingFactor = testTypeGroup === NATIVE_TEST_TYPE_GROUPS.WEB ? 1000 : 1;
    const unscaled = get(data, mapping, null);
    return unscaled != null ? unscaled / scalingFactor : null;
}

/**
 * Returns the property to pull from our data based on the metric provided. Note, not all metrics
 * are provided equal. Web and VoIP tests will have their own custom mappings because the metric names
 * do not match the values stored in Cassandra. I feel like this needs to be cleaned up in 2.6 or 3.0.
 *
 * @param {Object} metric
 * @param {String} testTypeGroup
 * @return {String}
 */
function getMetricMapping(metric, testTypeGroup) {
    const { name } = metric;
    let field = `avg${name}`;

    if (testTypeGroup === NATIVE_TEST_TYPE_GROUPS.WEB) {
        field = WEB_TEST_METRIC_MAPPING[name];
    } else if (testTypeGroup === NATIVE_TEST_TYPE_GROUPS.VOIP) {
        field = VOIP_TEST_METRIC_MAPPING[name];
    }

    return field;
}

/**
 * Finds the primary metric or first non-hidden metric if no (chartable) primary exists.
 *
 * @param {Object[]} metrics
 * @returns {Object}
 */
function findPrimaryMetric(metrics) {
    return find(metrics, { isMain: true, isString: false }) || find({ hidden: false });
}

/**
 * Filters the metrics in a test type to the ones we should see. The test type group matters
 * as well as the variant of operation in picking which one.
 *
 * @param {Object[]} metrics
 * @param {String} testTypeGroup
 * @param {Object} options
 * @return {Object[]}
 */
function filterMetrics(metrics, testTypeGroup, options) {
    const { variant } = options;
    const hasMain = some(metrics, { isMain: true });
    let atLeastOnePicked = false;

    return reject(metrics, (metric) => {
        if (metric.hidden) {
            // Only show hidden metrics if in overlay variant and the metric is primary, since we're meant to overlay
            // the primary metric's before and after.  Of course, if the primary metric is a string, don't show
            // it, since it can't be charted.
            return variant === VARIANT_OVERLAY ? !metric.isMain || metric.isString : true;
        }

        // Only show the primary metric for overlay charts, unless there is no primary metric. Then pick the first non-main, visible metric.
        if (variant === VARIANT_OVERLAY && ((!metric.isMain && hasMain) || atLeastOnePicked)) {
            return true;
        }

        // do not show the overall response time for web tests in breakdowns
        if (
            variant === VARIANT_BREAKDOWN &&
            testTypeGroup === NATIVE_TEST_TYPE_GROUPS.WEB &&
            metric.name === 'response_time'
        ) {
            return true;
        }

        // this metric looks good, let's include it!
        atLeastOnePicked = true;
        return false;
    });
}

export const getTrendData = (def, data) => {
    return data?.[def.dataIndex][def.key];
};

export const plotOptions = {
    series: {
        grouping: false,
        shadow: false,
        pointPlacement: null,
        stacking: null,
        groupPadding: null,
        borderWidth: 0,
        pointPadding: 0.2,
    },
};

export function getNumberPoints(series) {
    return max(series.map((singleSeries) => singleSeries?.data?.length || 0)) || 0;
}

function getAssociatedSeries(series) {
    const chartSeries = series?.chart?.series;
    return reject(chartSeries, (s) => s?.options?.showInTooltip === false);
}

/**
 * Finds all of the associated points for the given point.
 *
 * @param {Series[]} allSeries The array of all the series
 * @param {Point} point The point to find associated points for.
 * @return {Point[]}
 */
function getAssociatedPoints(allSeries, point) {
    const { testId } = point;
    return chain(allSeries).map('data').flatten().filter({ testId }).value();
}

/**
 * Queries all of the points in a chart which are in a given state
 *
 * @param {String} state
 * @param {H.Chart} chart
 * @return {H.Point[]}
 */
function getPointsByState(state, chart) {
    return chain(chart.series).map('data').flatten().filter({ state }).value();
}

/**
 * Highlights the provided wrapped points on a Highcharts chart
 *
 * @param {Point[]} points
 * @param {Object} chart
 */
function highlightPoints(points, chart) {
    points = filter(points, (point) => {
        const markerEnabled = point?.marker?.enabled !== false;
        const markerHoverEnabled = point?.marker?.states?.hover?.enabled !== false;

        return markerEnabled && markerHoverEnabled;
    });

    const highlightedPoints = getPointsByState('hover', chart);
    const pointsToHighlight = difference(points, highlightedPoints);
    const pointsToClear = difference(highlightedPoints, points);

    pointsToClear.forEach((p) => p.setState());
    pointsToHighlight.forEach((p) => p.setState('hover'));
}

export function highlightAssociatedPoints(point) {
    const { series } = point;
    if (!point.series) {
        return;
    }
    const { chart } = series;
    const allSeries = getAssociatedSeries(series);
    const allPoints = getAssociatedPoints(allSeries, point);
    highlightPoints(allPoints, chart);
}

export function unhighlightAssociatedPoints(point) {
    const { series } = point;
    if (!point.series) {
        return;
    }
    const { chart } = series;
    const highlightedPoints = getPointsByState('hover', chart);
    highlightedPoints.forEach((p) => p.setState());
}
